import { getAiService } from '../functions/aiService.js'

// AiActionConfirmCard — renders when sdk.ai.dispatch returns an
// 'action' intent in authMode='ask'. Shows the proposed SDK calls
// (create_ticket, create_event, …) with a one-line summary each and
// Confirm / Reject buttons. Confirm fires sdk.ai.executeActions and
// surfaces the resolved results in `_aiActionResults` so the caller
// can dismiss the card + show a success toast.
//
// Mounted at the workspace shell root with a `hide:` rule keyed off
// `_aiActionProposals`. Centered fixed-position card with a backdrop
// — same visual language as the AppWindow modal but tighter chrome
// (action cards are short, opinionated affordances; AppWindow is a
// page surface).
//
// FAMILY: elevated-alert, one of the two CTA notification families
// DESIGN_LANGUAGE.md §7.6 defines — `background: 'document'` +
// `round: 'radiusSheet'` + `shadow: 'overlay'` (§2.3's two-shadow
// budget), with CreditBlocker and CallInvitationBanner. Dismissal is
// durable root state, never a cookie. The coachmark family
// (TutorialNotification and siblings, `canvas-card-glass` +
// `radiusControl` + cookie dismissal) is deliberately separate: Nika
// decided that on 2026-08-29, CALLINVITATION-CONSOLIDATE-1 answer B —
// two families, one per urgency tier. Do not migrate this card onto
// the coachmark chassis; a soft onboarding surface must not be what
// asks the user to approve an SDK write.
// `packages/shared/__tests__/notificationFamilyChrome.test.js` locks it.
//
// State contract (root state — see workspace/state-app.js):
//   _aiActionProposals : Array<ProposedAction> | null  — set by the
//      caller after sdk.ai.dispatch returns. Each action has:
//        { kind, summary, sdkCall: { service, method, args } }
//   _aiActionRunning   : boolean  — true while the SDK calls are in
//      flight; disables the buttons + shows a spinner.
//   _aiActionError     : string | null  — last execution error.
//
// Close handlers:
//   - Reject button:  clear proposals, no SDK call.
//   - Confirm button: call sdk.ai.executeActions, write results to
//                     _aiActionResults, then clear proposals.
//   - Backdrop click: same as Reject.
//
// Per-action row uses childExtends: 'AiActionRow' so adding new
// action kinds (different icon/label) only requires updating the
// AiActionRow.icon factory.
export const AiActionConfirmCard = {
  hide: (el, s) => !s.root?._aiActionProposals?.length,
  // Open/closed through the kit's AppDialog lifecycle (DESIGN_SPEC §6.6): the
  // same reactive side effect AppDialog runs from its `isOpen` — focus moves
  // in when proposals arrive (the first control, Reject: the safe one) and
  // goes back to the opener when they clear. `dataOpen` is the reader that
  // keeps the effect running.
  isOpen: (el, s) => {
    const open = !!s.root?._aiActionProposals?.length
    el.call('appDialogSync', open)
    return open
  },
  dataOpen: (el) => !!el.isOpen,
  position: 'fixed',
  top: '0',
  left: '0',
  right: '0',
  bottom: '0',
  // The Modal.js layer (DESIGN_SPEC §6.8: PageSlot 1300 < DropdownRoot 1500 <
  // Modal 2000): a confirmation the composer raises must cover the open
  // window and the header. At z 95 it opened UNDER a PageSlot window and left
  // the header undimmed (live on dev 2026-09-26).
  zIndex: '2000',
  // THE blocking veil (DESIGN_SPEC §6.3): the `backdrop` theme — the scrim
  // fill and its 8px blur — fading in each time the card opens (a `hide:`
  // flip restarts the keyframe). It was the scrim + a raw `blur(6px)`.
  theme: 'backdrop',
  animationName: 'fadeIn',
  animationDuration: 'C',
  animationTimingFunction: 'defaultBezier',
  animationFillMode: 'both',
  align: 'center center',
  padding: 'overlayGutter',
  // The phone sheet touches the bottom edge.
  '@mobileL': { padding: '0', alignItems: 'flex-end' },

  onClick: (e, el, s) => {
    // Backdrop click dismisses (same as Reject). Inner Card stops
    // propagation so its own clicks don't bubble here.
    if (e.target === el.node) {
      s.root?.update?.({ _aiActionProposals: null, _aiActionError: null })
    }
  },
  // Escape = Reject, and it stops here (the top-most overlay only).
  onKeydown: (e, el, s) => {
    if (e.key !== 'Escape') return
    e.stopPropagation()
    s.root?.update?.({ _aiActionProposals: null, _aiActionError: null })
  },

  // The centre sheet (§6.1) at `overlayMd`, capped by the veil's inset box,
  // rising on the spring; the phone gets the bottom sheet. The chassis
  // (`document` + `radiusSheet` + `overlay`) is the elevated-alert family's
  // signature — kept (notificationFamilyChrome.test.js).
  Card: {
    position: 'relative',
    boxSizing: 'border-box',
    width: 'overlayMd',
    maxWidth: '100%',
    maxHeight: '100%',
    background: 'document',
    round: 'radiusSheet',
    shadow: 'overlay',
    overflow: 'hidden',
    flow: 'y',
    animationName: 'modalIn',
    animationDuration: 'D',
    animationTimingFunction: 'springBezier',
    animationFillMode: 'both',
    '@mobileL': {
      width: '100%',
      maxHeight: '92%',
      borderBottomLeftRadius: '0',
      borderBottomRightRadius: '0',
      animationName: 'fadeInUpShort'
    },
    role: 'dialog',
    ariaModal: 'true',
    ariaLabelledby: 'aiActionConfirmTitle',
    tabindex: '-1',
    onKeydown: (e, el) => el.call('appDialogTrapTab', e),
    onClick: (e) => e.stopPropagation(),

    Header: {
      flow: 'y',
      gap: 'V',
      padding: 'A B',
      // Was a `borderBottom` divider (the border habit) — Header/Body/Footer
      // already read as distinct zones via padding + typographic hierarchy
      // (bold H3 title vs. the row list vs. the button row), matching the
      // UserProfile precedent in this same sweep (design-direction.md §8).
      Title: {
        tag: 'h3',
        id: 'aiActionConfirmTitle',
        margin: '0',
        fontSize: 'B',
        fontWeight: '700',
        color: 'title',
        text: '{{ ai.confirm.title | polyglot }}'
      },
      Sub: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z2',
        color: 'caption',
        text: (el, s) => {
          const n = s.root?._aiActionProposals?.length || 0
          return n === 1
            ? 'Review the proposed change and confirm to apply.'
            : `Review the ${n} proposed changes and confirm to apply.`
        }
      }
    },

    // Per-action row list. childrenAs:'state' so each row gets the
    // ProposedAction shape as its state. The row reads s.kind + s.summary
    // to render.
    Body: {
      flow: 'y',
      gap: 'V',
      padding: 'A B',
      maxHeight: '50dvh',
      overflow: 'auto',
      childExtends: 'AiActionRow',
      childrenAs: 'state',
      children: (el, s) => s.root?._aiActionProposals || []
    },

    // Error banner — shown when executeActions throws. The proposals
    // stay rendered so the user can retry the same set.
    ErrorBanner: {
      hide: (el, s) => !s.root?._aiActionError,
      role: 'alert',
      padding: 'V A',
      background: 'red.1',
      // The AA-safe danger pair — the raw brand `red` reads under 4.5:1 as
      // text on its own tint.
      color: 'dangerInk',
      fontSize: 'Z2',
      text: (el, s) => `Couldn't apply: ${s.root?._aiActionError || ''}`
    },

    Footer: {
      flow: 'x',
      gap: 'A',
      padding: 'A B',
      align: 'center flex-end',
      // Was a `borderTop` divider — see the Header comment above.

      // The kit footer (DESIGN_SPEC §6.9): Reject = the ghost capsule, Confirm
      // = the ink primary with the house floor ("Confirm" → "Applying…").
      // Was a hand-built ghost and a raw `blue` plate.
      RejectBtn: {
        extends: 'AppCapsuleBtnGhost',
        disabled: (el, s) => !!s.root?._aiActionRunning,
        text: '{{ ai.confirm.reject | polyglot }}',
        onClick: (e, el, s) => {
          e.preventDefault()
          s.root?.update?.({
            _aiActionProposals: null,
            _aiActionError: null
          })
        }
      },

      ConfirmBtn: {
        extends: 'AppCapsuleBtnPrimary',
        minWidth: 'E',
        disabled: (el, s) => !!s.root?._aiActionRunning,
        text: (el, s) => (s.root?._aiActionRunning ? 'Applying…' : 'Confirm'),
        onClick: async (e, el, s) => {
          e.preventDefault()
          const root = s.root
          const actions = root?._aiActionProposals
          if (!Array.isArray(actions) || !actions.length) return
          const ai = getAiService()
          if (!ai) {
            root?.update?.({ _aiActionError: 'AI service not ready' })
            return
          }
          root?.update?.({ _aiActionRunning: true, _aiActionError: null })
          try {
            const results = await ai.executeActions(actions)
            const failed = results.find((r) => r?.error)
            if (failed) {
              root?.update?.({
                _aiActionRunning: false,
                _aiActionError: failed.error?.message || String(failed.error)
              })
              return
            }
            root?.update?.({
              _aiActionRunning: false,
              _aiActionProposals: null,
              _aiActionResults: results,
              _aiActionError: null
            })
          } catch (err) {
            root?.update?.({
              _aiActionRunning: false,
              _aiActionError: err?.message || String(err)
            })
          }
        }
      }
    }
  }
}

// AiActionRow — per-proposal row inside AiActionConfirmCard.Body.
// Receives the ProposedAction shape as state via childrenAs:'state'.
//
// Visual: icon tile (color-coded by kind) + summary line + small
// "kind" tag. Click-through is reserved for a future "edit before
// confirm" affordance; for now rows are read-only.
export const AiActionRow = {
  // FA204 / frank trap — the kind→{icon,color,label} lookup + its resolver
  // live in `scope`, not module scope: a bare module-scope const/function
  // silently reads as undefined once the component is frank.toJSON'd for
  // platform delivery (workspace d6ea1142, project_frank_drops_module_local_helpers).
  scope: {
    kindMeta: (kind) => {
      const KIND_META = {
        'ticket.create': { icon: 'check', color: 'orange', label: 'ai.confirm.kind.ticketCreate' },
        'ticket.update': { icon: 'check', color: 'orange', label: 'ai.confirm.kind.ticketUpdate' },
        'ticket.move': { icon: 'arrowRight', color: 'orange', label: 'ai.confirm.kind.ticketMove' },
        'event.create': {
          icon: 'date outline',
          color: 'blue',
          label: 'ai.confirm.kind.eventCreate'
        },
        'note.create': { icon: 'document', color: 'green', label: 'ai.confirm.kind.noteCreate' },
        'doc.create': { icon: 'document', color: 'cerulean', label: 'ai.confirm.kind.docCreate' },
        'kb.create': { icon: 'document', color: 'cerulean', label: 'ai.confirm.kind.kbCreate' }
      }
      return KIND_META[kind] || { icon: 'star', color: 'gray', label: kind || 'Action' }
    }
  },
  // Straight on the sheet (DESIGN_SPEC §6.2) — no `overlay` box per row.
  flow: 'x',
  gap: 'A',
  align: 'center',
  padding: 'V 0',

  IconTile: {
    width: 'C',
    height: 'C',
    minWidth: 'C',
    round: 'radiusControl',
    align: 'center center',
    background: (el, s) => el.scope.kindMeta(s.kind).color + '.2',
    color: (el, s) => el.scope.kindMeta(s.kind).color,
    Icon: {
      icon: (el, s) => el.scope.kindMeta(s.kind).icon,
      boxSize: 'A'
    }
  },

  Body: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',
    Summary: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => s.summary || el.call('polyglot', el.scope.kindMeta(s.kind).label)
    },
    Kind: {
      tag: 'span',
      fontSize: 'Y',
      color: 'caption',
      text: (el, s) => el.call('polyglot', el.scope.kindMeta(s.kind).label)
    }
  }
}
